/**
 * Subject Detail — "Medical History" section (2026-10-08).
 *
 * Past and ongoing conditions, from the CTMS schema spec sheet 2.
 * `meddra_code` is deliberately optional on entry: coding happens after
 * the fact and must never block a site from recording a condition.
 *
 * Write access mirrors the backend's CLINICAL_WRITE_ROLES exactly. The
 * server is still the authority — this only decides whether to show the
 * controls, so a stale role here cannot grant anything.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Stethoscope, Trash2, X } from 'lucide-react';
import {
  subjectClinical,
  type ConditionStatus,
  type MedicalHistoryRow,
} from '@/api/client';
import { fmtDate } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

const WRITE_ROLES = new Set([
  'coordinator',
  'principal_investigator',
  'sub_investigator',
  'clinical_nurse',
]);

const STATUS_LABELS: Record<ConditionStatus, string> = {
  resolved: 'Resolved',
  ongoing: 'Ongoing',
  intermittent: 'Intermittent',
};

const STATUS_BADGE: Record<ConditionStatus, string> = {
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ongoing: 'bg-amber-50 text-amber-700 border-amber-200',
  intermittent: 'bg-sky-50 text-sky-700 border-sky-200',
};

const EMPTY = {
  condition_verbatim: '',
  condition_status: 'ongoing' as ConditionStatus,
  meddra_code: '',
  onset_date: '',
  resolution_date: '',
};

export function MedicalHistorySection({ subjectId }: { subjectId: string }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.has(user?.role ?? '');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const listQ = useQuery({
    queryKey: ['subject-medical-history', subjectId],
    queryFn: () => subjectClinical.listMedicalHistory(subjectId),
    staleTime: 30_000,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['subject-medical-history', subjectId] });
  };

  const createM = useMutation({
    mutationFn: () =>
      subjectClinical.createMedicalHistory(subjectId, {
        condition_verbatim: form.condition_verbatim.trim(),
        condition_status: form.condition_status,
        ...(form.meddra_code.trim() ? { meddra_code: form.meddra_code.trim() } : {}),
        ...(form.onset_date ? { onset_date: form.onset_date } : {}),
        ...(form.resolution_date ? { resolution_date: form.resolution_date } : {}),
      }),
    onSuccess: () => {
      toast.success('Condition recorded.');
      setAdding(false);
      setForm(EMPTY);
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(errorText(e, 'Could not record the condition.')),
  });

  const deleteM = useMutation({
    mutationFn: (historyId: string) => subjectClinical.deleteMedicalHistory(subjectId, historyId),
    onSuccess: () => {
      toast.success('Entry removed.');
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(errorText(e, 'Could not remove the entry.')),
  });

  const rows = listQ.data?.rows ?? [];

  return (
    <section className="rounded border border-surface-border bg-white">
      <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Medical history</h2>
          {rows.length > 0 && <span className="text-xs text-ink-muted">({rows.length})</span>}
        </div>
        {canWrite && !adding && (
          <button type="button" className="btn-secondary" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add condition
          </button>
        )}
      </header>

      {adding && (
        <form
          className="px-4 py-3 border-b border-surface-border bg-surface-muted/30 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            createM.mutate();
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-ink-muted">
              Condition <span className="text-danger-600">*</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.condition_verbatim}
                maxLength={255}
                required
                onChange={(e) => setForm({ ...form, condition_verbatim: e.target.value })}
                placeholder="As recorded by the site"
              />
            </label>
            <label className="text-xs text-ink-muted">
              Status <span className="text-danger-600">*</span>
              <select
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.condition_status}
                onChange={(e) =>
                  setForm({ ...form, condition_status: e.target.value as ConditionStatus })
                }
              >
                {(Object.keys(STATUS_LABELS) as ConditionStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-ink-muted">
              Onset date
              <input
                type="date"
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.onset_date}
                onChange={(e) => setForm({ ...form, onset_date: e.target.value })}
              />
            </label>
            <label className="text-xs text-ink-muted">
              Resolution date
              <input
                type="date"
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink"
                value={form.resolution_date}
                onChange={(e) => setForm({ ...form, resolution_date: e.target.value })}
              />
            </label>
            <label className="text-xs text-ink-muted md:col-span-2">
              MedDRA code <span className="text-ink-muted">(optional — added when coded)</span>
              <input
                className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm text-ink font-mono"
                value={form.meddra_code}
                maxLength={50}
                onChange={(e) => setForm({ ...form, meddra_code: e.target.value })}
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary" disabled={createM.isPending}>
              {createM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Save condition
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setAdding(false);
                setForm(EMPTY);
              }}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        </form>
      )}

      {listQ.isLoading ? (
        <div className="p-8 text-center">
          <Loader2 className="h-4 w-4 animate-spin mx-auto text-ink-muted" />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-8 text-center text-sm text-ink-muted">
          No medical history recorded for this subject.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-surface-border">
              <tr>
                <th className="px-4 py-2">Condition</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Onset</th>
                <th className="px-4 py-2">Resolved</th>
                <th className="px-4 py-2">MedDRA</th>
                {canWrite && <th className="px-4 py-2 w-10" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r: MedicalHistoryRow) => (
                <tr key={r.history_id} className="border-b border-surface-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-ink">{r.condition_verbatim}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`px-1.5 py-0.5 rounded border text-[11px] font-medium ${STATUS_BADGE[r.condition_status]}`}
                    >
                      {STATUS_LABELS[r.condition_status]}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-ink-muted">{r.onset_date ? fmtDate(r.onset_date) : '—'}</td>
                  <td className="px-4 py-2.5 text-ink-muted">
                    {r.resolution_date ? fmtDate(r.resolution_date) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-ink-muted font-mono text-xs">
                    {r.meddra_code || '—'}
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        title="Remove entry"
                        className="text-ink-muted hover:text-danger-600"
                        disabled={deleteM.isPending}
                        onClick={() => deleteM.mutate(r.history_id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
